import React, { useState, useEffect, useCallback } from 'react'; // Aggiunto useCallback
import { Card, Form, ListGroup, Spinner, Alert, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getTaskLists, getTasksInList, updateTaskInList } from '../services/api'; // Aggiunto updateTaskInList
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faTasks, faPlus } from '@fortawesome/free-solid-svg-icons';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-toastify'; // Import toast per i messaggi

const AttivitaPage = () => {
    const navigate = useNavigate();
    const [taskLists, setTaskLists] = useState([]);
    const [selectedListId, setSelectedListId] = useState('');
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchTasks = useCallback(async () => { // fetchTasks come useCallback per evitare loop in useEffect
        if (selectedListId) {
            setLoading(true);
            setError('');
            try {
                const response = await getTasksInList(selectedListId);
                if (response.data && response.data.tasks) {
                    setTasks(response.data.tasks);
                }
            } catch (err) {
                console.error("Errore nel caricamento delle attività:", err);
                setError('Impossibile caricare le attività. Verifica la console per i dettagli.');
            } finally {
                setLoading(false);
            }
        } else {
            setTasks([]);
        }
    }, [selectedListId]); // Dipendenza: ricarica fetchTasks se selectedListId cambia

    useEffect(() => {
        const fetchTaskLists = async () => {
            try {
                const response = await getTaskLists();
                if (response.data && response.data.task_lists) {
                    setTaskLists(response.data.task_lists);
                    if (response.data.task_lists.length > 0) {
                        setSelectedListId(response.data.task_lists[0].id);
                    }
                }
            } catch (err) {
                console.error("Errore nel caricamento degli elenchi attività:", err);
                setError('Impossibile caricare gli elenchi attività. Verifica la console per i dettagli.');
            } finally {
                setLoading(false);
            }
        };
        fetchTaskLists();
    }, []);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]); // fetchTasks ora dipende da fetchTasks (useCallback)



    const handleListChange = (e) => {
        setSelectedListId(e.target.value);
    };

    const handleNewTaskClick = () => {
        if (selectedListId) {
            navigate('/attivita/nuovo', { state: { listId: selectedListId } });
        } else {
            toast.error("Seleziona prima una lista di attività.");
        }
    };

    const handleToggleTaskStatus = async (taskId, currentStatus) => {
        if (!selectedListId) {
            toast.error("Nessuna lista selezionata per aggiornare l'attività.");
            return;
        }

        const newStatus = currentStatus === 'completed' ? 'needsAction' : 'completed';
        try {
            await updateTaskInList(selectedListId, taskId, { status: newStatus });
            toast.success(`Attività marcata come "${newStatus}".`); // Ho tolto taskId da qui per una questione di UX, per il momento
            fetchTasks(); // Ricarica le attività per aggiornare la UI
        } catch (err) {
            console.error("Errore nell'aggiornamento dello stato dell'attività:", err);
            toast.error("Errore durante l'aggiornamento dello stato dell'attività.");
        }
    };

    if (loading && taskLists.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Caricamento Attività...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div className="container-fluid">
            <h1 className="mb-4">Le Tue Attività Google</h1>
            {/* CARD PER GOOGLE TASKS */}
            <Card className="shadow-sm mb-4">
                <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faTasks} className="me-2" /> Gestione Attività
                    </div>
                    <Button variant="primary" size="sm" onClick={handleNewTaskClick} disabled={!selectedListId}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" /> Nuova Attività
                    </Button>
                </Card.Header>
                <Card.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Seleziona Lista Attività:</Form.Label>
                        <Form.Control as="select" value={selectedListId} onChange={handleListChange}>
                            {taskLists.length > 0 ? (
                                taskLists.map(list => (
                                    <option key={list.id} value={list.id}>
                                        {list.title}
                                    </option>
                                ))
                            ) : (
                                <option value="">Nessuna lista trovata</option>
                            )}
                        </Form.Control>
                    </Form.Group>

                    {tasks.length > 0 ? (
                        <ListGroup>
                            {tasks.map(task => (
                                <ListGroup.Item key={task.id} className="d-flex align-items-center">
                                    <FontAwesomeIcon 
                                        icon={task.status === 'completed' ? faCheckCircle : faCircle} 
                                        className={`me-3 ${task.status === 'completed' ? 'text-success' : 'text-muted'}`}
                                        onClick={() => handleToggleTaskStatus(task.id, task.status)} // Rendi l'icona cliccabile
                                        style={{ cursor: 'pointer' }} // Aggiungi stile per indicare che è cliccabile
                                    />
                                    <div>
                                        <strong>{task.title}</strong>
                                        {task.notes && <p className="text-muted mb-0">{task.notes}</p>}
                                        {task.due && (
                                            <p className="text-info mb-0">
                                                Scadenza: {format(parseISO(task.due), 'dd/MM/yyyy HH:mm')}
                                            </p>
                                        )}
                                    </div>
                                    <div className="ms-auto">
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            onClick={() => navigate(`/attivita/modifica/${selectedListId}/${task.id}`)} // Nuovo pulsante Modifica
                                            className="me-2"
                                        >
                                            Modifica
                                        </Button>
                                        {/* Link esterno per aprire su Google Tasks, se esiste */}
                                        {/* <Button variant="outline-info" size="sm" href={`https://tasks.google.com/embed/?id=${task.id}`} target="_blank">
                                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                                        </Button> */}
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    ) : (
                        <Alert variant="info">Nessuna attività non completata trovata per questa lista.</Alert>
                    )}
                </Card.Body>
            </Card>


        </div>
    );

};

export default AttivitaPage;
